// Role Manager - Utility functions for role-based access control
// This file imports configuration from roles.config.js and provides utility functions

import {
  ROLE_IDS,
  ROLE_NAMES,
  ROLE_DISPLAY_NAMES,
  ROLE_HIERARCHY,
  PAGE_PERMISSIONS,
  MENU_SECTION_PERMISSIONS,
  MENU_ITEM_PERMISSIONS,
  FEATURE_PERMISSIONS,
  ROLE_GROUPS
} from '../config/roles.config.js';

// Re-export role constants for backward compatibility
export const ROLES = ROLE_IDS;
export { ROLE_IDS };

// Function to check if a user has access to a specific page
export const hasPageAccess = (userRole, pagePath) => {
  if (!userRole) return false;
  
  // Check exact path match first
  if (PAGE_PERMISSIONS[pagePath]) {
    return PAGE_PERMISSIONS[pagePath].includes(userRole);
  }
  
  // Check pattern matches (for dynamic routes like /projects/:id)
  for (const [pattern, allowedRoles] of Object.entries(PAGE_PERMISSIONS)) {
    if (pattern.includes(':')) {
      const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${regexPattern}$`);
      if (regex.test(pagePath)) {
        return allowedRoles.includes(userRole);
      }
    }
  }
  
  return false;
};

// Function to check if a user has access to a menu section
export const hasMenuSectionAccess = (userRole, sectionName) => {
  if (!userRole) return false;
  return MENU_SECTION_PERMISSIONS[sectionName]?.includes(userRole) || false;
};

// Function to check if a user has access to a menu item
export const hasMenuItemAccess = (userRole, itemName) => {
  if (!userRole) return false;
  return MENU_ITEM_PERMISSIONS[itemName]?.includes(userRole) || false;
};

// Function to check if a user has access to a specific feature
export const hasFeatureAccess = (userRole, featureName) => {
  if (!userRole) return false;
  return FEATURE_PERMISSIONS[featureName]?.includes(userRole) || false;
};

// Function to get user's role hierarchy level
export const getUserRoleLevel = (userRole) => {
  return ROLE_HIERARCHY[userRole] || 0;
};

// Function to check if user has minimum role level
export const hasMinimumRole = (userRole, minimumRole) => {
  const userLevel = getUserRoleLevel(userRole);
  const minimumLevel = getUserRoleLevel(minimumRole);
  return userLevel >= minimumLevel;
};

// Function to get role display name
export const getRoleDisplayName = (roleId) => {
  return ROLE_DISPLAY_NAMES[roleId] || 'Unknown Role';
};

// Function to get role name for database
export const getRoleName = (roleId) => {
  return ROLE_NAMES[roleId] || 'unknown';
};

// Function to get all roles that can access a specific feature
export const getRolesForFeature = (featureName) => {
  return FEATURE_PERMISSIONS[featureName] || [];
};

// Function to check if user can manage other users (admin and people manager only)
export const canManageUsers = (userRole) => {
  return hasFeatureAccess(userRole, 'MANAGE_USERS');
};

// Function to check if user can manage projects (managers and above)
export const canManageProjects = (userRole) => {
  return hasFeatureAccess(userRole, 'MANAGE_PROJECTS');
};

// Function to check if user can manage customers
export const canManageCustomers = (userRole) => {
  return hasFeatureAccess(userRole, 'MANAGE_CUSTOMERS');
};

// Function to check if user can view manager reports
export const canViewManagerReports = (userRole) => {
  return hasFeatureAccess(userRole, 'VIEW_MANAGER_REPORTS');
};

// Function to check if user can manage teams
export const canManageTeams = (userRole) => {
  return hasFeatureAccess(userRole, 'MANAGE_TEAMS');
};

// Function to check if user can manage worklogs
export const canManageWorklogs = (userRole) => {
  return hasFeatureAccess(userRole, 'MANAGE_WORKLOGS');
};

// Function to check if user can delete projects (admin and service manager only)
export const canDeleteProjects = (userRole) => {
  if (!userRole) return false;
  // Only ADMIN and SERVICE_MANAGER can delete projects
  return userRole === ROLE_IDS.ADMIN || userRole === ROLE_IDS.SERVICE_MANAGER;
};

// Function to check if user belongs to a role group
export const isInRoleGroup = (userRole, groupName) => {
  if (!userRole || !ROLE_GROUPS[groupName]) return false;
  return ROLE_GROUPS[groupName].includes(userRole);
};

// Function to get all available roles
export const getAllRoles = () => {
  return Object.entries(ROLE_DISPLAY_NAMES).map(([id, name]) => ({
    id: parseInt(id),
    name: name,
    code: getRoleName(parseInt(id))
  }));
};

// Function to validate if a role ID is valid
export const isValidRole = (roleId) => {
  return Object.values(ROLE_IDS).includes(roleId);
};
